// Unified, app-wide search index — the data behind the ⌘K command palette.
//
// Design notes:
//   • Dependency-free. Azzurro ships no runtime libraries (its Markdown renderer,
//     conjugation engine, etc. are all hand-rolled), so search is too. For a
//     few-thousand-item corpus a normalised substring + prefix matcher is
//     instant (<5 ms) and — unlike a loose fuzzy threshold — never surfaces
//     junk for short Spanish queries (the risk the design research flagged).
//   • Diacritic-insensitive: "esta" finds "está", "n" tolerates "ñ". We fold
//     accents on BOTH the query and the indexed text.
//   • Weighted: a hit in a title (the infinitive / term / article title) ranks
//     above a hit buried in a translation or example sentence.
//
// The index merges every content pillar plus the app's own pages, so the
// palette doubles as a "jump to anywhere" navigator.
import { fetchVerbs, fetchArticles, fetchVocabulary } from "./data.js";
import { EXPRESSIONS } from "../data/expressions.js";

// Fold to lowercase ASCII-ish: strip diacritics so á/é/í/ó/ú/ñ compare loosely.
export function fold(s) {
  return String(s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim();
}

// Per-type display metadata (section header label + glyph + group order).
export const TYPE_META = {
  verb: { label: "Verbs", emoji: "🔤", order: 0 },
  grammar: { label: "Grammar", emoji: "📖", order: 1 },
  vocabulary: { label: "Vocabulary", emoji: "🗂️", order: 2 },
  expression: { label: "Expressions", emoji: "🎭", order: 3 },
  page: { label: "Pages", emoji: "🧭", order: 4 },
};

// Static app destinations so "search across the whole page" can jump to any
// section — not just content rows. Keywords pad the haystack for fuzzy intent.
const PAGES = [
  { title: "Start", subtitle: "Home & learning paths", nav: "dashboard", kw: "home start overview paths map" },
  { title: "Verb Search", subtitle: "Find & conjugate any verb", nav: "search", kw: "verbs conjugate dictionary" },
  { title: "Verb Groups", subtitle: "Regular, stem-changing, irregular", nav: "groups", kw: "patterns families irregular" },
  { title: "Tenses & Moods", subtitle: "Every tense at a glance", nav: "tenses", kw: "present preterite imperfect future subjunctive imperative conditional" },
  { title: "Grammar Overview", subtitle: "The whole-grammar guide", nav: "guide", kw: "overview guide reference" },
  { title: "Nouns", subtitle: "Gender & number", nav: "nouns", kw: "gender plural masculine feminine" },
  { title: "Adjectives", subtitle: "Agreement & position", nav: "adjectives", kw: "agreement position descriptive" },
  { title: "Sentence Structure", subtitle: "Word order & negation", nav: "structure", kw: "syntax word order questions negation" },
  { title: "Vocabulary", subtitle: "Themed word cards", nav: "vocabulary", kw: "words themes nouns" },
  { title: "Common Expressions", subtitle: "Idioms & set phrases", nav: "expressions", kw: "idioms sayings phrases" },
  { title: "Reference Shelf", subtitle: "Infographic posters", nav: "reference", kw: "posters infographics cheatsheet" },
  { title: "Conjugation Trainer", subtitle: "Practice conjugating", nav: "trainer", kw: "practice quiz drill" },
  { title: "Flashcards", subtitle: "Vocabulary flashcards", nav: "flashcards", kw: "practice cards review" },
  { title: "Fill the Gap", subtitle: "Cloze practice", nav: "cloze", kw: "practice gap blank" },
  { title: "Spot the Slip", subtitle: "Error-correction practice", nav: "correction", kw: "practice mistakes errors" },
  { title: "Due for Review", subtitle: "Spaced review", nav: "review", kw: "practice spaced repetition srs" },
];

// Build one normalised, weighted entry. `title` is the primary match field,
// `subtitle` secondary, `extra` (examples/keywords) lowest weight.
function entry(type, title, subtitle, nav, params, extra = "") {
  return {
    type,
    title,
    subtitle,
    nav,
    params: params || {},
    _t: fold(title),
    _s: fold(subtitle),
    _h: fold(`${title} ${subtitle} ${extra}`),
  };
}

let _indexPromise = null;

// Build (and memoise) the full index. Fetches every public pillar once through
// the same hybrid data layer the views use, so it works offline too.
export function buildSearchIndex() {
  if (_indexPromise) return _indexPromise;
  _indexPromise = (async () => {
    const [verbs, articles, vocab] = await Promise.all([
      fetchVerbs().catch(() => []),
      fetchArticles().catch(() => []),
      fetchVocabulary().catch(() => []),
    ]);

    const out = [];

    for (const v of verbs) {
      out.push(
        entry(
          "verb",
          v.infinitive,
          v.english_translation || "",
          "verb-detail",
          { inf: v.infinitive },
          v.note || ""
        )
      );
    }

    for (const a of articles) {
      out.push(
        entry(
          "grammar",
          a.title,
          `${a.difficulty_level || ""} · ${a.category || "grammar"}`.replace(/^ · /, ""),
          "article",
          { slug: a.slug },
          a.category || ""
        )
      );
    }

    for (const c of vocab) {
      out.push(
        entry(
          "vocabulary",
          c.spanish,
          c.english,
          "vocabulary",
          { theme: c.theme },
          `${c.example_es || ""} ${c.example_en || ""} ${c.theme || ""}`
        )
      );
    }

    for (const x of EXPRESSIONS) {
      out.push(
        entry(
          "expression",
          x.spanish,
          x.meaning,
          "expressions",
          { cat: x.category },
          `${x.literal || ""} ${x.example_es || ""} ${x.example_en || ""}`
        )
      );
    }

    for (const p of PAGES) {
      out.push(entry("page", p.title, p.subtitle, p.nav, {}, p.kw));
    }

    return out;
  })();
  return _indexPromise;
}

// Score one entry against a folded query + its tokens. Higher = better.
// Tiered so a title hit always beats a subtitle hit beats a deep haystack hit.
function score(e, qn, tokens) {
  let s = 0;
  if (e._t === qn) s = 1000;
  else if (e._t.startsWith(qn)) s = 760 - Math.min(e._t.length, 40);
  else if (e._t.includes(` ${qn}`)) s = 640; // word-start inside the title
  else if (e._t.includes(qn)) s = 520;

  if (s < 460) {
    if (e._s.startsWith(qn)) s = Math.max(s, 440);
    else if (e._s.includes(qn)) s = Math.max(s, 340);
  }
  if (s < 280 && e._h.includes(qn)) s = Math.max(s, 240);

  // Multi-word fallback: every token present somewhere (any field).
  if (s === 0 && tokens.length > 1 && tokens.every((t) => e._h.includes(t))) {
    s = 120;
  }
  // Tiny, stable type tiebreaker (verbs/grammar nudge above vocab/pages).
  return s > 0 ? s + (4 - TYPE_META[e.type].order) : 0;
}

// Rank the index against a query. Returns up to `limit` scored entries,
// newest-best-first, each with its winning `_score` attached.
export function searchEntries(index, query, limit = 24) {
  const qn = fold(query);
  if (!qn) return [];
  const tokens = qn.split(/\s+/).filter(Boolean);
  const scored = [];
  for (const e of index) {
    const sc = score(e, qn, tokens);
    if (sc > 0) scored.push({ e, _score: sc });
  }
  scored.sort((a, b) => b._score - a._score || a.e._t.localeCompare(b.e._t));
  return scored.slice(0, limit).map((r) => ({ ...r.e, _score: r._score }));
}

// Group ranked results by type for display, capping each group. Groups are
// ordered by their single best-scoring hit so the most relevant type leads.
export function groupResults(results, perGroup = 6) {
  const groups = new Map();
  for (const r of results) {
    if (!groups.has(r.type)) groups.set(r.type, []);
    const arr = groups.get(r.type);
    if (arr.length < perGroup) arr.push(r);
  }
  return Array.from(groups.entries())
    .map(([type, items]) => ({ type, items, top: items[0]?._score || 0 }))
    .sort((a, b) => b.top - a.top);
}
